/** Where the player is in the walk: the step the address names (`?step=`), kept beside `?desk=` and `?mode=review`, and the rule for a step that no longer exists. */
import { useEffect, useState } from "react";
import type { DecisionFamily } from "@bsim/domain";
import { navigate } from "../../shell/history";
import { decisionReviewState, openDecisionsOf } from "../reviewState";
import { currentOrResume, parseStepKey, resumeStep, stepKey, walkSteps, type WalkInput, type WalkStep } from "./walkSteps";

/** Opening position: the address's step if it exists; else the first To-review (else first) decision of `?desk=`; else the resume step. */
function openingKey(i: WalkInput, steps: readonly WalkStep[], search: string): string | null {
  const params = new URLSearchParams(search);
  const named = parseStepKey(params.get("step"), steps);
  if (named !== null) return stepKey(named);
  const desk = params.get("desk");
  const own = steps.filter((s): s is Extract<WalkStep, { kind: "decision" }> => s.kind === "decision" && s.desk === desk);
  const pick = own.find((s) => decisionReviewState(i.draft, s.decisionId) === "toReview") ?? own[0];
  if (pick !== undefined) return stepKey(pick);
  const resume = resumeStep(i);
  return resume === null ? null : stepKey(resume);
}

/** The page tab a step sits on: its desk, or the Events tab for the steps that belong to no desk. */
export const tabOfStep = (s: WalkStep): DecisionFamily | "events" => (s.kind === "decision" || s.kind === "rationale" ? s.desk : "events");

export function useWalkPosition(i: WalkInput, search: string): {
  readonly steps: readonly WalkStep[]; readonly current: WalkStep | null; readonly goTo: (s: WalkStep) => void; readonly resume: () => void; readonly firstOf: (desk: DecisionFamily) => WalkStep | null;
} {
  const steps = walkSteps({ ...i, optionalThesis: new URLSearchParams(search).get("step") === "thesis" });
  const [key, setKey] = useState<string | null>(() => openingKey(i, steps, search));
  const goTo = (s: WalkStep): void => {
    setKey(stepKey(s));
    const params = new URLSearchParams(window.location.search);
    params.set("step", stepKey(s));
    navigate(`${window.location.pathname}?${params.toString()}`, { replace: true });
  };
  const resume = (): void => { const r = resumeStep(i); setKey(r === null ? null : stepKey(r)); };
  const firstOf = (desk: DecisionFamily): WalkStep | null => openDecisionsOf(i.draft, desk, i.catalog)[0] === undefined ? null : steps.find((s) => s.kind === "decision" && s.desk === desk) ?? null;
  const current = currentOrResume(i, steps, key);
  // The address always names the step on screen (the rail's guide reads it to mark the step the player is on).
  const shown = current === null ? null : stepKey(current);
  useEffect(() => {
    if (shown === null) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("mode") === "review" || params.get("step") === shown) return;
    params.set("step", shown);
    navigate(`${window.location.pathname}?${params.toString()}`, { replace: true });
  }, [shown]);
  return { steps, current, goTo, resume, firstOf };
}
